import { zonedInputToISO } from "../../lib/format";

export type RecurrencePattern = "none" | "weekly" | "biweekly" | "monthly";

export interface BookingOccurrenceInput {
  start: string;
  end: string;
  setupMinutes: number;
  teardownMinutes: number;
}

interface RecurrenceInput {
  startLocal: string;
  endLocal: string;
  timezone: string;
  pattern: RecurrencePattern;
  count: number;
  setupMinutes: number;
  teardownMinutes: number;
}

interface RecurrenceResult {
  occurrences: BookingOccurrenceInput[];
  recurrenceRule: string;
}

const localDateTimePattern = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

function parseWallClock(value: string): Date {
  const match = localDateTimePattern.exec(value);
  if (!match) throw new Error("Enter a valid start and end time.");
  const [, year, month, day, hour, minute] = match;
  const result = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)),
  );
  if (
    result.getUTCFullYear() !== Number(year) ||
    result.getUTCMonth() !== Number(month) - 1 ||
    result.getUTCDate() !== Number(day) ||
    result.getUTCHours() !== Number(hour) ||
    result.getUTCMinutes() !== Number(minute)
  ) {
    throw new Error("Enter a valid calendar date and time.");
  }
  return result;
}

function formatWallClock(value: Date): string {
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${value.getUTCFullYear()}-${pad(value.getUTCMonth() + 1)}-${pad(value.getUTCDate())}T${pad(value.getUTCHours())}:${pad(value.getUTCMinutes())}`;
}

function shiftStart(value: Date, pattern: RecurrencePattern, index: number): Date {
  const result = new Date(value);
  if (pattern === "weekly" || pattern === "biweekly") {
    result.setUTCDate(result.getUTCDate() + index * (pattern === "weekly" ? 7 : 14));
    return result;
  }
  if (pattern === "monthly") {
    const originalDay = result.getUTCDate();
    const targetMonth = result.getUTCMonth() + index;
    const targetYear = result.getUTCFullYear() + Math.floor(targetMonth / 12);
    const normalizedMonth = ((targetMonth % 12) + 12) % 12;
    const lastDay = new Date(Date.UTC(targetYear, normalizedMonth + 1, 0)).getUTCDate();
    result.setUTCFullYear(targetYear, normalizedMonth, Math.min(originalDay, lastDay));
  }
  return result;
}

export function buildRecurrence(input: RecurrenceInput): RecurrenceResult {
  const start = parseWallClock(input.startLocal);
  const end = parseWallClock(input.endLocal);
  const duration = end.getTime() - start.getTime();
  if (duration <= 0) throw new Error("The booking end must be after its start.");
  if (!Number.isInteger(input.setupMinutes) || input.setupMinutes < 0 || input.setupMinutes > 1440)
    throw new Error("Setup minutes must be between 0 and 1,440.");
  if (
    !Number.isInteger(input.teardownMinutes) ||
    input.teardownMinutes < 0 ||
    input.teardownMinutes > 1440
  )
    throw new Error("Teardown minutes must be between 0 and 1,440.");

  const count = input.pattern === "none" ? 1 : input.count;
  if (input.pattern !== "none" && (!Number.isInteger(count) || count < 2 || count > 52))
    throw new Error("A repeating booking must contain between 2 and 52 occurrences.");

  const occurrences = Array.from({ length: count }, (_, index) => {
    const occurrenceStart = shiftStart(start, input.pattern, index);
    const occurrenceEnd = new Date(occurrenceStart.getTime() + duration);
    return {
      start: zonedInputToISO(formatWallClock(occurrenceStart), input.timezone),
      end: zonedInputToISO(formatWallClock(occurrenceEnd), input.timezone),
      setupMinutes: input.setupMinutes,
      teardownMinutes: input.teardownMinutes,
    };
  });

  const recurrenceRule =
    input.pattern === "none"
      ? ""
      : `FREQ=${input.pattern === "monthly" ? "MONTHLY" : "WEEKLY"};INTERVAL=${input.pattern === "biweekly" ? 2 : 1};COUNT=${count}`;
  return { occurrences, recurrenceRule };
}
